/**
 * 작성자 : 홍선영
 * 날짜 : 2024.11.19
 * 기능 : 근태양식지 각 아이템
 */

import styled from 'styled-components';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { shortCkeditorConfig, shortCkeditorConfigWithoutImg } from '../../../services/ckeditorConfig';
import { ClassicEditor } from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import Input from '../../../components/Input';
import NumbersInput from '../../../components/NumbersInput';
import Textarea from '../../../components/Textarea';
import DOMPurify from 'dompurify';
import { IApplicationFormType, Time } from 'customTypes';
import ClickableContainer from '../../h_cctv/RealtimeCCTV/ClickableContainer';
import TableLamp from '../../../components/TableLamp';
import { useFetchCommonCodeList } from '../../../services/useSetCodeListInSelectBoxForm';
import { COMCD_ATTENDANCE_GUBUN, INIT_END_HMS, INIT_START_HMS } from '../../../_constants';
import RangePicker from '../../../components/RangePicker';
import useDateRange from '../../../hooks/useDateRange';
import CustomTimePicker from '../../../components/CustomTimePicker';
import dayjs from 'dayjs';
import SmallTextLabel from '../../../components/button/SmallTextLabel';
import { renderLineStatus } from '../../../utils/renderApprovalStatus';
import { applyBorderStyle } from '../../../utils/applyBorderStyle';
import DatePickerComponent from '../../../components/DatePicker';
import { isMobile } from '../../../utils/checkMobile';

interface Cell {
  cdName: string;
  width: number;
  height: number;
  aRow: number | null;
  aCol: number | null;
  isDropped: boolean; // Drop 여부
  subCd: string;
  aCd: string;
  aContents: string;
}

interface IRoot {
  type: 'init_form' | 'view' | 'edit';
}

const Root = styled.div<IRoot>`
  display: flex;
  width: 100%;
  color: var(--text-primary);
  font-weight: 600;

  .inputMask {
    margin-left: 0.8rem !important;
    height: 2rem;
  }

  // 인쇄 전용 css
  &.print {
    --print-color: #18181b;
    color: #000;

    .sign,
    &.sign {
      border-color: var(--print-color);
      border-bottom: 2px solid var(--print-color);

      .vertical-text {
        border-color: var(--print-color);
      }

      .flex-column {
        border-color: var(--print-color);

        .sign-space {
          background-color: #fff;
          border-color: var(--print-color);
        }

        > .small-title:last-child {
          border-top-color: var(--print-color);
        }
      }
    }

    &.input {
      input,
      .dash {
        color: var(--print-color);
        padding: 0;
      }

      .empty-input {
        background-color: #fff;
      }

      .nums {
        > :last-child {
          input {
            font-size: 0.625rem !important;
          }
        }
      }
    }

    &.textarea {
      textarea {
        border-color: #d5d5d5;
        background-color: #fff;
        color: var(--print-color);
      }
    }

    .dateRange {
      > :first-child > div {
        #startInput2,
        #endInput2 {
          background-color: #fff;
        }
      }

      > :nth-child(2) {
        > div {
          > div > button {
            color: ${({ type }: IRoot) => (type === 'view' ? 'var(--print-color)' : '')};
          }
        }

        @media screen and (max-width: 767px) {
          padding: 0 0.5rem;
        }
      }
    }

    &.textarea {
      > .input-large {
        background-color: #fff;
      }
    }
  }

  .label {
    font-weight: 600;
  }

  &.subcd-6 {
    display: flex;
    flex-direction: column;
    z-index: 1;
    padding-left: 0.75rem;
    .sign {
      flex-grow: 1;
      > div {
        height: 6.5rem;
      }

      .sign-space {
        border-bottom: none !important;

        .noUse > span {
          display: ${({ type }) => (type !== 'edit' ? 'none' : '')};
        }

        span:hover {
          box-shadow: ${({ type }) => (type === 'view' ? 'none' : '')};
          background-color: ${({ type }) => (type === 'view' ? 'transparent' : '')};
          cursor: ${({ type }) => (type === 'edit' ? 'pointer' : 'default')};
        }
      }
      @media screen and (max-width: 767px) {
        flex-grow: 0;
      }
    }

    .text {
      padding: 0.25rem 0;
    }

    @media screen and (max-width: 767px) {
      padding: 0;
    }
  }

  &.sign {
    height: inherit;
  }

  .sign,
  &.sign {
    display: flex;
    align-items: center;
    width: 100%;
    border: 1px solid var(--text-primary);

    > :last-child {
      border-right: none !important;
    }

    .vertical-text {
      writing-mode: vertical-lr;
      padding: 0.5rem;
      border-right: 1px solid var(--text-primary);
      height: inherit;
      justify-content: center;
      display: flex;
    }

    .flex-column {
      display: flex;
      flex-direction: column;
      /* width: 24%; */
      height: inherit;
      flex-grow: 1;
      text-align: center;
      justify-content: space-between;
      border-right: 1px solid var(--text-primary);
      flex: 1 1 0;
      min-width: 0;

      .small-title {
        padding: 0.5rem 0;
        min-height: 2.5rem;

        &.text-small {
          font-size: 0.875rem;
          padding: 0.25rem;
          gap: 0.25rem;
          > div {
            line-height: 1;
          }
        }
      }

      .sign-space {
        flex-grow: 1;
        background-color: var(--board);
        border-top: 1px solid var(--text-primary);
        display: flex;
        justify-content: center;
        align-items: center;
        height: 58px;
        padding: 4px 0;

        img {
          // width: 70%;
          width: auto;
          min-width: 1rem;
          max-width: 5rem;
          height: 100%;
        }
      }

      > .small-title:last-child {
        border-top: 1px solid var(--text-primary);
      }
    }
  }

  &.input {
    padding: 0.75rem;
    > :last-child {
      flex-grow: 1;
      > div {
        flex-grow: 1;
        > input {
          z-index: 1;
          background-color: ${({ type }: IRoot) => (type === 'view' ? 'transparent' : '')};
          padding: 0 0.5rem;
        }
      }
    }
    .label {
      display: flex;
      width: 4rem;
      min-width: fit-content;
      align-items: center;
    }
    // 날짜 입력칸은 래퍼 폭(100%)에 좌측 여백(.inputMask margin-left 0.8rem)이 더해져 오른쪽이 옆 칸에 가려지므로 여백만큼 폭을 줄임
    // (모바일은 아래 미디어쿼리에서 여백 0·폭 100%로 별도 처리)
    @media screen and (min-width: 768px) {
      .datePickerWrapper .inputMask {
        width: calc(100% - 0.8rem);
      }
    }
    .empty-input {
      background-color: var(--board);
      height: 2rem;
      border-radius: 5px;
      flex-grow: 1;
      text-align: center;
      align-items: center;
      display: flex;
      padding: 0 0.5rem;
      justify-content: flex-start;
    }

    // 모바일: 3열 그리드(33% 폭)에서 라벨+날짜/인풋이 한 줄에 안 들어가 겹치는 문제 해결.
    // 라벨을 위로 세로 스택하고, 인풋/데이트픽커를 셀 폭 100%로 가둠.
    // 그리드 행 높이(CELL_HEIGHT 46px)는 고정이므로, 스택 내용이 그 안에 들어가도록 패딩/라벨/인풋 높이를 압축.
    @media screen and (max-width: 767px) {
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      gap: 0;
      padding: 0.2rem;

      .label {
        width: 100%;
        min-width: 0;
        font-size: 0.7rem;
        line-height: 1.1;
      }

      // 마지막 자식(인풋/데이트픽커 래퍼)이 셀 폭을 넘지 않도록 100%로 고정
      > :last-child {
        width: 100%;
        min-width: 0;
        > div {
          width: 100%;
          min-width: 0;
        }
      }

      .datePickerWrapper {
        width: 100%;
        min-width: 0;
      }

      // 46px 행 안에 라벨(1줄)+입력이 함께 들어가도록 입력 높이를 축소.
      > :last-child input,
      .inputMask {
        margin-left: 0 !important;
        width: 100%;
        box-sizing: border-box;
        height: 1.6rem;
        font-size: 0.72rem;
      }

      .empty-input {
        height: 1.6rem;
      }
    }

    &.nums {
      gap: ${({ type }: IRoot) => (type === 'view' ? '0' : '0.5rem')};
      > :last-child {
        input {
          padding: 0rem;
          background-color: ${({ type }: IRoot) => (type === 'view' ? 'transparent' : '')};
          font-size: 0.875rem;
        }
      }
      @media screen and (max-width: 767px) {
        flex-wrap: wrap;
        gap: 0;
        > :last-child {
          input {
            height: fit-content;
          }

          .dash {
            padding: 0;
          }
        }
      }
    }
  }

  &.off-period {
    height: inherit;
    .info {
      display: flex;
      gap: 0.75rem;
    }

    .label {
      width: 4.5rem;
      padding-left: 0.75rem;
      display: flex;
      align-items: center;
    }

    @media screen and (max-width: 767px) {
      height: 5rem;

      .label {
        width: 3rem;
        padding: 0;
      }
    }
  }

  .dateRange {
    z-index: 999;
    width: inherit;
    padding: 0.5rem;

    > :first-child > div {
      > div {
        gap: 0.5rem;
        margin-top: 0.125rem;
      }

      #startInput2,
      #endInput2 {
        padding: ${({ type }: IRoot) => (type === 'view' ? '0' : '0 1rem')};
        background-color: var(--tonal);
        &:hover {
          background-color: var(--tonal-deep);
        }

        @media screen and (max-width: 767px) {
          padding: 0 0.5rem;
          min-width: 4rem;
          height: fit-content;
        }
      }
    }

    > :nth-child(2) {
      padding: 0 1rem;

      > div {
        gap: 0.5rem;
        margin-top: 0.125rem;

        > div > button {
          height: 2.25rem;
          padding: ${({ type }: IRoot) => (type === 'view' ? '0' : '0 1rem')};
          color: ${({ type }: IRoot) => (type === 'view' ? 'var(--text-primary)' : '')};
          width: ${({ type }: IRoot) => (type === 'view' ? 'fit-content' : ' 4.5rem')};
          font-size: 1rem;
        }
      }

      @media screen and (max-width: 767px) {
        min-width: fit-content;
        padding: 0 0.5rem !important;
        position: absolute;
        z-index: 1;

        > div > div > button {
          width: 2.5rem;
          font-size: 100%;
          height: fit-content;
        }
      }
    }

    .empty-span {
      text-align: end;
    }

    .check {
      > input {
        margin-left: 1rem;
        position: relative;
        top: 0.2rem;
      }

      @media screen and (max-width: 767px) {
        > input {
          top: 0rem !important;
        }
      }

      label {
        cursor: pointer;
        user-select: none;
      }
    }

    > div {
      display: inline-block;
    }

    .rangePickerWrapper > div {
      left: 91%;

      .rdrDefinedRangesWrapper {
        display: none;
      }
    }

    .inputForm-col {
      width: fit-content;
      > div {
        display: flex;
        flex-direction: column;
        width: fit-content;
      }

      &.hover {
        background-color: var(--tonal-deep);
      }
    }

    .inputForm-col:focus-within {
      background-color: var(--tonal-deep) !important;
      border-radius: 5px;
      > div > input {
        background-color: var(--tonal-deep) !important;
      }
    }

    #startInput2,
    #endInput2 {
      height: 2.25rem;
      border-radius: 0.25rem;
      width: auto;
      min-width: 6rem;
      padding: 0;
      text-align: center;
      outline: none;
      font-weight: 500;
      background-color: ${({ type }) => `${type !== 'edit' ? 'transparent' : ''} !important`};
      z-index: 1;

      cursor: ${({ type }) => (type === 'edit' ? 'pointer' : 'default')};

      > span {
        padding: 0 0.5rem;
      }
    }

    .flex-basic {
      button {
        cursor: ${({ type }) => `${type === 'edit' ? 'pointer' : 'default'} !important`};
        &.disabled {
          background-color: ${({ type }) => (type === 'view' ? 'transparent' : '')};
        }
        &:hover {
          background-color: ${({ type }) => (type === 'view' ? 'transparent' : '')};
        }
      }
    }

    .date-period {
      position: relative;
      top: -1.2rem;

      @media screen and (max-width: 767px) {
        top: -3.2rem;
        display: flex;
        justify-content: flex-end;
        align-items: center;
      }
    }
  }

  .empty-span {
    width: 2.5rem;
    display: inline-block;
  }

  &.textarea {
    height: inherit;
    padding-bottom: 0.5rem;
    .label {
      display: flex;
      align-items: center;
      padding-left: 0.75rem;
      width: 4.5rem;
      word-break: keep-all;
      justify-content: space-between;
      // 모바일: 라벨이 길면(예: "참고사항" 4글자) keep-all + min-width:auto 탓에 라벨 박스가 글자폭만큼 늘어나
      // textarea를 오른쪽으로 민다. 글자영역을 2글자(2em)로 제한해 "참고/사항"처럼 2줄로 줄바꿈시키고,
      // flex-shrink:0 + 우측 패딩으로 박스 총폭은 일정하게 유지해 다른 textarea와 시작 위치를 맞춘다.
      @media screen and (max-width: 767px) {
        // column flex: 세로 가운데 정렬 유지 + 2em 폭(cross축 stretch) 안에서 텍스트가 예측 가능하게 줄바꿈
        display: flex;
        flex-direction: column;
        justify-content: center; // 세로 가운데 정렬
        align-items: stretch; // 텍스트 박스가 2em 폭을 채워 줄바꿈되도록 (flex-start면 한 줄로 안 줄어듦)
        text-align: left;
        box-sizing: content-box;
        width: 2em; // 글자영역 = 2글자 (폰트 크기와 무관하게 2글자에서 줄바꿈)
        min-width: 0;
        flex-shrink: 0;
        padding: 0 0.75rem 0 0;
        word-break: break-all; // keep-all 무력화: 공백 없는 한글도 줄바꿈 허용
        white-space: normal;
        line-height: 1.2;
      }
    }

    > .input-large {
      background-color: var(--board);
      border-radius: 5px;
      flex-grow: 1;
      margin: 0.75rem;
    }

    > .editor-wrapper {
      position: relative;
      z-index: 999;
    }

    &.edit,
    &.view {
      width: 100%;

      > :last-child {
        width: 100%;
        // 라벨 옆 flex 항목이라, 최소 폭이 auto(내용 최소폭)면 브라우저 폭을 줄였을 때 줄어들지 못하고
        // 라벨 폭만큼 오른쪽으로 넘쳐 내용 끝이 잘린다 → 셀 폭 안으로 줄어들 수 있게 함
        min-width: 0;
        padding: 0 0.68rem;
        height: 100%;

        textarea {
          min-height: auto;
          z-index: 1;
          padding: 0.5rem;
        }

        &.editor-wrapper {
          display: flex;
          flex-direction: column;
        }

        .ck-editor {
          flex: 1;
          height: 100%;
          width: 100%;
        }

        .ck-editor__main {
          height: calc(100% - 2.5rem);
        }

        .ck-editor__editable {
          height: 100%;
          min-height: auto;
        }

        .ck-widget__selection-handle {
          display: none;
        }

        .ck-widget.ck-widget_selected,
        .ck-widget.ck-widget_selected:hover {
          outline: none;
        }
      }
    }

    &.view {
      > .editor-wrapper {
        .ck-editor__main {
          height: 100%;
        }
      }
    }

    // 모바일: CKEditor 툴바가 좁은 폭에서 여러 줄로 줄바꿈되면 .ck-editor__main의 height:calc(100% - 2.5rem)
    // (툴바 1줄 가정)이 어긋나 에디터 전체가 셀 높이를 초과 → 아래 참고사항(subCd 24)과 겹친다.
    // flex로 셀 안에 가두고, 툴바는 실제 높이를 차지하되 편집영역이 남은 공간을 채우며 내부 스크롤하도록 함.
    @media screen and (max-width: 767px) {
      &.edit > .editor-wrapper,
      &.view > .editor-wrapper {
        overflow: hidden;

        .ck-editor {
          display: flex;
          flex-direction: column;
          min-height: 0;
        }
        .ck-editor__top {
          flex: 0 0 auto; // 툴바: 자기 높이(1줄)만큼만 차지
        }
        // 툴바를 1줄로 고정하고 넘치면 가로 스크롤 (shouldNotGroupWhenFull:true라 기본은 여러 줄로 줄바꿈됨)
        .ck.ck-toolbar {
          flex-wrap: nowrap;
          overflow-x: auto;
          overflow-y: hidden;
          -webkit-overflow-scrolling: touch;
        }
        .ck.ck-toolbar > .ck-toolbar__items {
          flex-wrap: nowrap;
        }
        // 가로 스크롤 시 버튼이 찌그러지지 않도록 고정
        .ck.ck-toolbar > .ck-toolbar__items > * {
          flex: 0 0 auto;
        }
        .ck-editor__main {
          flex: 1 1 auto;
          height: auto; // 고정 calc 무력화 → 남은 공간 채움
          min-height: 0;
          overflow: auto; // 내용은 셀 안에서 스크롤
        }
        .ck-editor__editable {
          height: 100%;
          min-height: 0;
        }
      }
    }
  }

  &.sender {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    > :last-child {
      padding-top: 1.5rem;
    }
  }

  &.plain-text {
    padding: 0.75rem;

    &.center {
      text-align: center;
      justify-content: center;
    }

    &.left {
      justify-content: start;
    }

    &.title-large {
      font-weight: 600;
      font-size: 2.5rem;
      letter-spacing: 0.5rem;

      @media screen and (max-width: 767px) {
        & {
          font-size: 1.5rem;
          letter-spacing: 0.2rem;
          padding: 0.2rem;
        }
      }
    }
  }

  &.editor {
    padding: 0.75rem;
    display: flex;
    flex-direction: column;
    height: inherit;
    z-index: 999;

    .label-wrapper {
      display: flex;
      justify-content: space-between;
      button {
        height: 2rem;
      }
    }

    .editor-wrapper {
      gap: 0.5rem;
      /* padding-top: 0.5rem; */
      width: inherit;

      .ck-editor {
        width: -webkit-fill-available;
      }

      .ck-editor__main > div {
        /* height: 6.5rem; */
      }

      button {
        height: inherit;
      }
    }
  }

  &.logo {
    padding: 0.75rem;
    width: 100%;
    justify-content: end;
    img {
      width: 9rem;
    }
  }

  /* 모바일 한 줄 배치(기안서·근태계 작성/보기, 화면 폭 767px 이하, stackedFormLayout 참고).
     항목이 폭 전체를 쓰므로 3열 격자용 모바일 압축(라벨 위로 올림·0.7rem 글자·1.6rem 입력칸·2글자 라벨)을 되돌리고
     모든 항목을 「라벨 | 값」 한 줄, 같은 글자 크기로 맞춘다. 격자용 규칙보다 뒤에 두고 선택자를 한 단계 더 구체적으로 함 */
  // 결재란(sign)·근태 구분 표(subcd-6)는 표 모양 그대로 두기 위해 글자 크기 통일에서 제외
  &.stacked:not(.sign):not(.subcd-6) {
    font-size: 0.875rem;

    .label {
      font-size: 0.875rem;
      line-height: 1.4;
    }
  }

  &.input.stacked {
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 0.5rem;
    padding: 0.375rem 0.75rem;

    .label {
      width: 5rem;
      min-width: 5rem;
      flex-shrink: 0;
    }

    > :last-child {
      flex: 1;
      width: auto;
      min-width: 0;
    }

    > :last-child input,
    .inputMask {
      height: 2rem;
      font-size: 0.875rem;
    }

    .inputMask {
      margin-left: 0 !important;
      width: 100%;
    }

    .empty-input {
      height: 2rem;
    }

    &.nums {
      flex-wrap: nowrap;
    }
  }

  // 여러 줄 입력(글상자·편집기, Root에 textarea 클래스가 붙는 항목)은 라벨을 위에 두고 상자가 폭 전체를 쓴다 (좁은 폭에서 본문 폭 확보)
  &.textarea.stacked {
    flex-direction: column;

    .label {
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
      box-sizing: border-box;
      width: auto;
      min-width: 0;
      flex-shrink: 0;
      padding: 0.375rem 0.75rem 0.25rem;
      word-break: keep-all;
      line-height: 1.4;
    }

    // 라벨이 위로 가므로 높이 100% 대신 남은 높이를 채움 (지정 높이를 넘지 않도록 min-height 0)
    > :last-child {
      flex: 1;
      height: auto;
      min-height: 0;
      padding: 0 0.75rem;
    }
  }

  &.plain-text.stacked:not(.title-large) {
    padding: 0.375rem 0.75rem;
  }
`;

const INIT_WORKING_HOUR_START_HMS = { h: 9, m: 0, s: 0 };
const INIT_WORKING_HOUR_END_HMS = { h: 18, m: 0, s: 0 };

// 인쇄본 CKEditor는 CSS 캐스케이드(고정 height 등)에 기대지 않고, 인라인 스타일로 직접
// 고정 높이/스크롤을 제거해 내용 길이만큼 늘어나도록 강제함 (react-to-print가 여러 페이지로 나눠 인쇄하도록)
const forcePrintEditorAutoHeight = (editor: any) => {
  const editableElement: HTMLElement | null = editor.ui.getEditableElement?.() ?? null;
  const mainElement = editableElement?.closest('.ck-editor__main') as HTMLElement | null;
  const editorElement = editableElement?.closest('.ck-editor') as HTMLElement | null;
  [editableElement, mainElement, editorElement].forEach((el) => {
    if (el) {
      el.style.setProperty('height', 'auto', 'important');
      el.style.setProperty('max-height', 'none', 'important');
      el.style.setProperty('overflow', 'visible', 'important');
    }
  });
};

// 툴바만 숨기면(toolbarElement.style.display='none') 그걸 감싸는 .ck-sticky-panel__content가
// 자체 border(상/좌/우, border-bottom-width:0)를 그대로 남겨 위쪽에 빈 테두리가 겹쳐 보임.
// 툴바를 담는 sticky panel 전체를 숨겨야 그 테두리까지 함께 사라짐.
const setEditorToolbarVisible = (editor: any, visible: boolean) => {
  const stickyPanelElement: HTMLElement | undefined = editor.ui.view.stickyPanel?.element;
  if (stickyPanelElement) {
    stickyPanelElement.style.display = visible ? '' : 'none';
  } else {
    editor.ui.view.toolbar.element.style.display = visible ? 'flex' : 'none';
  }

  // CSS(subcd_18/21)는 툴바가 위쪽 모서리를 담당한다고 가정하고 편집영역(.ck-content) 위쪽을 각지게 처리함.
  // 툴바(sticky panel)를 숨기면 편집영역 자체가 박스 최상단이 되므로 위쪽 모서리도 둥글게 보정해야 함.
  // (overflow는 건드리지 않음: 화면에서 내용이 길 때 내부 스크롤이 필요한 뷰 모드에 영향을 주지 않기 위함)
  // 어느 요소가 실제 배경/테두리를 그리는지 확실치 않아 편집영역과 바깥 wrapper(.ck-editor) 모두에 적용.
  const editableElement: HTMLElement | null = editor.ui.getEditableElement?.() ?? null;
  const editorElement = editableElement?.closest('.ck-editor') as HTMLElement | null;
  [editableElement, editorElement].forEach((el) => {
    if (!el) return;
    if (visible) {
      el.style.removeProperty('border-top-left-radius');
      el.style.removeProperty('border-top-right-radius');
    } else {
      el.style.setProperty('border-top-left-radius', '6px', 'important');
      el.style.setProperty('border-top-right-radius', '6px', 'important');
    }
  });
};

const ApplicationFormItems = (
  itemState: { type: 'init_form' | 'view' | 'edit'; data: any | null; formType: IApplicationFormType },
  subCd: string,
  name: string,
  cell: Cell,
  setCells: Dispatch<SetStateAction<Cell[]>>,
  logo: string,
  userInput?: any,
  setUserInput?: any,
  isSaveClicked?: boolean,
  selectedAttD1List?: { label: string; value: string }[],
  className?: string,
  editorContent?: string,
  setEditorContent?: Dispatch<SetStateAction<string>>
) => {
  const { type, data } = itemState;
  const [numberInput, setNumberInput] = useState({ num1: '', num2: '', num3: '' });
  const [positionList, setPositionList] = useState<{ label: string; value: string }[]>([]); // 직급명 메뉴 리스트
  const { data: caGubunComCdList } = useFetchCommonCodeList(COMCD_ATTENDANCE_GUBUN, false); // 근태 구분 목록
  const [caGubun, setCaGubun] = useState(''); // 근태 구분 선택
  const [_, setVisibleRangePicker] = useState(false);
  const [visibleRangePicker2, setVisibleRangePicker2] = useState(false);
  const { dateRange, setDateRange, userInputDateRange } = useDateRange('day');
  const [selectedTimeStart, setSelectedTimeStart] = useState(INIT_START_HMS);
  const [selectedTimeEnd, setSelectedTimeEnd] = useState(INIT_START_HMS);
  const [availableTimeRange, setAvailableTimeRange] = useState<{ start: Time; end: Time }>({ start: INIT_START_HMS, end: INIT_END_HMS });
  const [isAllday, setIsAllday] = useState(false); // 기간 선택 종일 여부

  useEffect(() => {
    if (data) {
      setUserInput({
        wDate: data.wDate,
        eDate: data.eDate,
        cCd: data.cCd,
        caCd: data.caCd,
        status: data.status,
        name: data.name,
        department: data.department,
        delYn: data.delYn,
        date: data.date,
        title: data.title,
        content: data.content,
        note: data.note,
        address: data.address,
        emerNum: data.emerNum,
        caGubun: data.caGubun,
        sDate: data.sDate,
        fDate: data.fDate,
        caPdate: data.caPdate,
        caPtime: data.caPtime,
        caReason: data.caReason,
        caReplacement: data.caReplacement,
      });

      if (data.sDate && data.fDate) {
        setDateRange([{ startDate: dayjs(data.sDate?.split(' ')[0]).toDate(), endDate: dayjs(data.fDate?.split(' ')[0]).toDate(), key: 'selection' }]);
      } else {
        setDateRange([{ startDate: new Date(), endDate: new Date(), key: 'selection' }]);
      }

      if (data.sDate?.split(' ')[1]) {
        const time = data.sDate?.split(' ')[1]?.split(':');
        setSelectedTimeStart({ h: Number(time[0]), m: Number(time[1]), s: 0 });
      } else {
        setSelectedTimeStart(INIT_WORKING_HOUR_START_HMS);
      }

      if (data.fDate?.split(' ')[1]) {
        const time = data.fDate?.split(' ')[1]?.split(':');
        setSelectedTimeEnd({ h: Number(time[0]), m: Number(time[1]), s: 0 });
      } else {
        setSelectedTimeEnd(INIT_WORKING_HOUR_END_HMS);
      }

      const splitNum = data.emerNum ? data.emerNum.split('-') : ['', '', ''];
      setNumberInput({ num1: splitNum[0] ?? '', num2: splitNum[1] ?? '', num3: splitNum[2] ?? '' });

      setCaGubun(data.caGubun);
    }
  }, [data]);

  useEffect(() => {
    if (selectedAttD1List) {
      setPositionList(selectedAttD1List);
    }
  }, [selectedAttD1List]);

  useEffect(() => {
    if (type === 'edit') {
      const sDate = userInputDateRange.start?.replaceAll('-', '');
      const fDate = userInputDateRange.end?.replaceAll('-', '');
      const sTime = `${String(selectedTimeStart.h).padStart(2, '0')}${String(selectedTimeStart.m).padStart(2, '0')}`;
      const fTime = `${String(selectedTimeEnd.h).padStart(2, '0')}${String(selectedTimeEnd.m).padStart(2, '0')}`;
      const sDateResult = `${sDate}${sTime}`;
      const fDateResult = `${fDate}${fTime}`;
      const caPdate = calculateDuration(userInputDateRange, selectedTimeStart, selectedTimeEnd).days;
      const caPtime = calculateDuration(userInputDateRange, selectedTimeStart, selectedTimeEnd).time.replace(':', '');
      setUserInput && setUserInput((prev: any) => ({ ...prev, sDate: sDateResult, fDate: fDateResult, caPdate, caPtime }));
    }
  }, [userInputDateRange.start, userInputDateRange.end, selectedTimeStart, selectedTimeEnd]);

  useEffect(() => {
    const isWithinWorkingHour = selectedTimeStart.h === 9 && selectedTimeStart.m === 0 && selectedTimeEnd.h === 18 && selectedTimeEnd.m === 0;
    if (isWithinWorkingHour) setIsAllday(true);
    else setIsAllday(false);
  }, [selectedTimeStart, selectedTimeEnd]);

  useEffect(() => {
    if (type !== 'init_form') setUserInput((prev: any) => ({ ...prev, caGubun }));
  }, [caGubun]);

  useEffect(() => {
    if (type !== 'init_form') {
      const mergedNum = `${numberInput.num1}-${numberInput.num2}-${numberInput.num3}`;
      setUserInput((prev: any) => ({ ...prev, emerNum: mergedNum !== '--' ? mergedNum : '' }));
    }
  }, [numberInput]);

  useEffect(() => {
    if (type !== 'init_form') {
      // 기간 종일 선택한 경우 시간 09~18로 업데이트
      if (isAllday) {
        setSelectedTimeStart({ h: 9, m: 0, s: 0 });
        setSelectedTimeEnd({ h: 18, m: 0, s: 0 });
      }
    }
  }, [isAllday]);

  // 기간 계산 함수
  const calculateDuration = useCallback(
    (range: { start: string; end: string }, startTime: Time, endTime: Time) => {
      const startDate = new Date(range.start);
      const endDate = new Date(range.end);

      // Case 1: 시작일과 종료일이 같고, 워킹아워일 때 (09:00-18:00)
      if (range.start === range.end && startTime.h === 9 && startTime.m === 0 && startTime.s === 0 && endTime.h === 18 && endTime.m === 0 && endTime.s === 0) {
        return { days: 1, time: '00:00' };
      }

      // Case 2: 시작일과 종료일이 같지만, 워킹아워가 아닐 때 (종일 아님)
      if (range.start === range.end) {
        const startSeconds = startTime.h * 3600 + startTime.m * 60 + startTime.s;
        const endSeconds = endTime.h * 3600 + endTime.m * 60 + endTime.s;
        let totalSeconds = endSeconds - startSeconds;

        // 점심시간(11:30~12:30)이 포함되면 그 시간만큼 차감
        const lunchStart = 11 * 3600 + 30 * 60; // 11:30
        const lunchEnd = 12 * 3600 + 30 * 60; // 12:30

        // 점심시간이 포함된 경우, 차감
        if (startSeconds < lunchEnd && endSeconds > lunchStart) {
          const overlapStart = Math.max(startSeconds, lunchStart);
          const overlapEnd = Math.min(endSeconds, lunchEnd);
          totalSeconds -= overlapEnd - overlapStart;
        }

        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);

        return { days: 0, time: `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}` };
      }

      // Case 3: 시작일과 종료일이 다르고 모두 종일 근무(09:00~18:00)일 때
      const isFullStart = startTime.h === 9 && startTime.m === 0 && startTime.s === 0;
      const isFullEnd = endTime.h === 18 && endTime.m === 0 && endTime.s === 0;

      if (isFullStart && isFullEnd) {
        let totalDays = 0;
        const currentDate = new Date(startDate);
        while (currentDate <= endDate) {
          const dayOfWeek = currentDate.getDay();
          if (dayOfWeek !== 0 && dayOfWeek !== 6) totalDays += 1;
          currentDate.setDate(currentDate.getDate() + 1);
        }
        return { days: totalDays, time: '00:00' };
      }

      // Case 4: 첫 날 또는 마지막 날이 반차인 경우 (startTime≠09:00 또는 endTime≠18:00)
      const isSameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
      const WORK_START_SEC = INIT_WORKING_HOUR_START_HMS.h * 3600 + INIT_WORKING_HOUR_START_HMS.m * 60;
      const WORK_END_SEC = INIT_WORKING_HOUR_END_HMS.h * 3600 + INIT_WORKING_HOUR_END_HMS.m * 60;
      const LUNCH_START_SEC = 11 * 3600 + 30 * 60; // 11:30
      const LUNCH_END_SEC = 12 * 3600 + 30 * 60; // 12:30

      const deductLunch = (fromSec: number, toSec: number): number => {
        let total = toSec - fromSec;
        if (fromSec < LUNCH_END_SEC && toSec > LUNCH_START_SEC) {
          total -= Math.min(toSec, LUNCH_END_SEC) - Math.max(fromSec, LUNCH_START_SEC);
        }
        return Math.max(0, total);
      };

      let fullDays = 0;
      let partialSeconds = 0;
      const countDate = new Date(startDate);

      while (countDate <= endDate) {
        const dayOfWeek = countDate.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          const isFirst = isSameDay(countDate, startDate);
          const isLast = isSameDay(countDate, endDate);

          if (isFirst && !isFullStart) {
            // 첫 날 반차: startTime ~ 18:00
            const startSec = startTime.h * 3600 + startTime.m * 60 + startTime.s;
            partialSeconds += deductLunch(startSec, WORK_END_SEC);
          } else if (isLast && !isFullEnd) {
            // 마지막 날 반차: 09:00 ~ endTime
            const endSec = endTime.h * 3600 + endTime.m * 60 + endTime.s;
            partialSeconds += deductLunch(WORK_START_SEC, endSec);
          } else {
            fullDays += 1;
          }
        }
        countDate.setDate(countDate.getDate() + 1);
      }

      const hours = Math.floor(partialSeconds / 3600);
      const minutes = Math.floor((partialSeconds % 3600) / 60);
      return { days: fullDays, time: `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}` };
    },
    [userInputDateRange.start, userInputDateRange.end, selectedTimeStart, selectedTimeEnd]
  );
  if (subCd === '1') {
    if (type === 'init_form') {
      const signField = ['담당', '부서장', '고문이사', '대표이사'];
      return (
        <Root type={type} className={`sign ${className}`}>
          <div className='title vertical-text label'>소속부서</div>
          {signField.map((el: string, index: number) => (
            <div key={index} className='flex-column label'>
              <div className='small-title'>{el}</div>
              <div className='sign-space' />
              <div className='small-title'>/</div>
            </div>
          ))}
        </Root>
      );
    }
    return (
      <Root type={type} className={`sign ${className}`}>
        <div className='title vertical-text label'>소속부서</div>
        {positionList?.length > 0 ? (
          positionList.map((el: any) => (
            <div key={`${el.value}_${el.positionName}`} className='flex-column'>
              <div className='small-title text-small flex-col flex-center'>
                <div>
                  {el.partName}/{el.positionName}
                </div>
                {/* <div>{el.label}</div> */}
              </div>
              <div className='sign-space'>
                {/** 싸인이미지가 있고, lstatus=2 승인상태일 때만 이미지 렌더링 */}
                {el.signImg && el.caLstatus === '2' ? (
                  <img src={el.signImg} alt='user_sign' />
                ) : (
                  <SmallTextLabel size='small' text={renderLineStatus(el.caLstatus).text} color={renderLineStatus(el.caLstatus).color} />
                )}
              </div>
              <div className='small-title'>{el.caDate ?? el.date}</div>
            </div>
          ))
        ) : (
          <div className='flex-column label'>
            <div className='small-title'>결재 라인을 선택하세요</div>
            <div className='sign-space' />
            <div className='small-title'>/</div>
          </div>
        )}
      </Root>
    );
  }

  if (['2', '3', '19'].includes(subCd)) {
    const { formType } = itemState;

    const keyMap: Record<string, string> = {
      '2': formType === 'attendance' ? 'caName' : 'name',
      '3': formType === 'attendance' ? 'caDepartment' : 'department',
      '19': 'date',
    };

    const key = keyMap[subCd];

    return (
      <Root type={type} className={`input ${className}`}>
        <div className='label'>{name}</div>
        {type === 'init_form' ? <div className='empty-input' /> : <div className='empty-input'>{data[key]}</div>}
      </Root>
    );
  }

  if (subCd === '4') {
    return (
      <Root type={type} className={`input nums ${className}`}>
        <div className='label'>{name}</div>
        {type === 'init_form' ? (
          <div className='empty-input' />
        ) : (
          <NumbersInput state={numberInput} setState={setNumberInput} lengthState={{ num1: 3, num2: 4, num3: 4 }} disabled={type !== 'edit'} isSaveClicked={isSaveClicked} />
        )}
      </Root>
    );
  }

  if (subCd === '5' || subCd === '20') {
    const key = subCd === '5' ? 'address' : 'title';
    return (
      <Root type={type} className={`input ${className}`}>
        <div className='label'>{name}</div>
        {type === 'init_form' ? (
          <div className='empty-input' />
        ) : (
          <Input
            type='text'
            id={key}
            name={key}
            state={userInput}
            setState={setUserInput}
            disabled={type !== 'edit'}
            getBorderStyle={isSaveClicked ? applyBorderStyle(userInput[key], 'red', key) : undefined}
          />
        )}
      </Root>
    );
  }

  if (subCd === '6') {
    const handleSelectAttendanceGubun = (caGubunParam: string) => {
      if (type === 'edit') setCaGubun(caGubunParam);
    };

    return (
      <Root type={type} className={`subcd-6 ${className}`}>
        <div className='label text'>해당란에 V표 하십시오.</div>
        <div className='sign' style={caGubun === '' && isSaveClicked ? { outline: '2px solid red', border: 'none' } : { outline: '' }}>
          <div className='title vertical-text label'>구분</div>
          {caGubunComCdList?.map((el: any) => (
            <div key={el[COMCD_ATTENDANCE_GUBUN]} className='flex-column label'>
              <div className='small-title'>{el.cdName}</div>
              {type === 'init_form' ? (
                <div className='sign-space' />
              ) : (
                <div className='sign-space'>
                  <div>
                    <ClickableContainer onClick={() => handleSelectAttendanceGubun(el[COMCD_ATTENDANCE_GUBUN])} button={<TableLamp use={caGubun === el[COMCD_ATTENDANCE_GUBUN]} />} />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </Root>
    );
  }

  if (subCd === '7') {
    if (type === 'init_form') {
      return (
        <Root type={type} className={`off-period flex-between ${className}`}>
          <div className='label'>기간</div>
          <div className='flex-center dateRange'>
            <div>
              <div>
                20
                <div className='empty-span' />년 <div className='empty-span' />월 <div className='empty-span' />일 <div className='empty-span' />
                요일 까지
              </div>
              <div>
                20
                <div className='empty-span' />년 <div className='empty-span' />월 <div className='empty-span' />일 <div className='empty-span' />
                요일 까지
              </div>
            </div>
            <div>
              ( <div className='empty-span' /> )일 / ( <div className='empty-span' /> : <div className='empty-span' /> )
            </div>
          </div>
        </Root>
      );
    }

    const onClickRangeInput = () => {
      if (!visibleRangePicker2 && type === 'edit') setVisibleRangePicker2(true);
    };

    const handleChangeAllday = () => {
      setIsAllday((prev) => !prev);
    };

    // data prop에서 직접 파생: state 비동기 업데이트 없이 즉시 올바른 값 사용
    const parseTimeStr = (timeStr: string | undefined, fallback: Time): Time => {
      if (!timeStr) return fallback;
      const p = timeStr.split(':');
      return { h: Number(p[0] ?? 0), m: Number(p[1] ?? 0), s: 0 };
    };
    const sRaw = data?.sDate?.split(' ');
    const fRaw = data?.fDate?.split(' ');
    const directStart = sRaw?.[0] || dayjs(dateRange[0]?.startDate).format('YYYY-MM-DD');
    const directEnd = fRaw?.[0] || dayjs(dateRange[0]?.endDate).format('YYYY-MM-DD');
    const directTimeStart = parseTimeStr(sRaw?.[1], INIT_WORKING_HOUR_START_HMS);
    const directTimeEnd = parseTimeStr(fRaw?.[1], INIT_WORKING_HOUR_END_HMS);

    // view: data에서 직접(즉시), edit: state(피커 상호작용 지원)
    const displayStart = type !== 'edit' ? directStart : dayjs(dateRange[0]?.startDate).format('YYYY-MM-DD');
    const displayEnd = type !== 'edit' ? directEnd : dayjs(dateRange[0]?.endDate).format('YYYY-MM-DD');
    const displayTimeStart = type !== 'edit' ? directTimeStart : selectedTimeStart;
    const displayTimeEnd = type !== 'edit' ? directTimeEnd : selectedTimeEnd;

    return (
      <Root type={type} className={`off-period ${className}`}>
        <div className='label'>기간</div>
        <div className='dateRange'>
          <div>
            <div className='flex-column'>
              <div className='flex-basic'>
                <div className='flex-center' id='startInput2' role='presentation' onMouseDown={onClickRangeInput}>
                  {displayStart}
                </div>
                <span>{`(${dayjs(displayStart).format('dd')})`}</span>
              </div>
              <div className='flex-basic'>
                <div className='flex-center' id='endInput2' role='presentation' onMouseDown={onClickRangeInput}>
                  {displayEnd}
                </div>
                <span>{`(${dayjs(displayEnd).format('dd')})`}</span>
              </div>
            </div>
            {type === 'edit' && visibleRangePicker2 && (
              <div className='rangePickerWrapper'>
                <RangePicker state={dateRange} setState={setDateRange} setVisible={setVisibleRangePicker} setVisible2={setVisibleRangePicker2} />
              </div>
            )}
          </div>
          <div>
            <div className='flex-basic'>
              <CustomTimePicker
                type='start'
                mode='picker'
                selectedTime={displayTimeStart}
                setSelectedTime={setSelectedTimeStart}
                availableTimeList={[{ start: INIT_START_HMS, end: INIT_END_HMS }]}
                availableTimeRange={availableTimeRange}
                setAvailableTimeRange={setAvailableTimeRange}
                isNewSchedule
                showSecond={false}
                useAutoRef={false}
                isDisabled={type !== 'edit' || isAllday}
                minUnit={30}
                hourRange={[9, 17]}
              />
              <div>부터</div>
            </div>
            <div className='flex-basic'>
              <CustomTimePicker
                type='end'
                mode='picker'
                startTime={displayTimeStart}
                selectedTime={displayTimeEnd}
                setSelectedTime={setSelectedTimeEnd}
                availableTimeList={[{ start: INIT_START_HMS, end: INIT_END_HMS }]}
                availableTimeRange={availableTimeRange}
                setAvailableTimeRange={setAvailableTimeRange}
                isNewSchedule
                showSecond={false}
                useAutoRef={false}
                isDisabled={type !== 'edit' || isAllday}
                minUnit={30}
                hourRange={[10, 18]}
              />
              <div>까지</div>
            </div>
          </div>
          {!isMobile() && (
            <div className='date-period'>
              <div className='flex-basic'>
                <div>
                  <div className='empty-span'>{calculateDuration({ start: displayStart, end: displayEnd }, displayTimeStart, displayTimeEnd).days}</div> 일 / (
                  <div className='empty-span'>{calculateDuration({ start: displayStart, end: displayEnd }, displayTimeStart, displayTimeEnd).time}</div> )
                </div>
              </div>
            </div>
          )}
          {type === 'edit' && (
            <div className='flex-center check date-period'>
              <input type='checkbox' id='allday' disabled={type !== 'edit'} checked={isAllday} onChange={handleChangeAllday} />
              <label htmlFor='allday' aria-disabled={type !== 'edit'}>
                종일
              </label>
            </div>
          )}
        </div>
      </Root>
    );
  }

  if (subCd === '22') {
    // 기안서 시행일자
    if (type === 'init_form') {
      return (
        <Root type={type} className={`input ${className}`}>
          <div className='label'>{name}</div>
          <div className='empty-input' />
        </Root>
      );
    }
    const key = 'date';
    // 값이 비었을 때 오늘로 '보이게만' 하면 실제 값(빈 값)과 화면이 달라, 상신 시 「시행일자를 입력하세요」가 날짜가 보이는데도 뜰 수 있음
    // → 화면을 실제 값과 일치시킨다 (새 기안은 DraftForm에서 시행일자를 오늘로 채워 열림)
    const startDate = userInput[key]?.replaceAll('-', '') || '';
    const setDate = (date: any) => {
      // DatePicker는 effect에서 호출하므로 클로저의 userInput 대신 최신 상태에 합친다
      setUserInput((prev: any) => ({ ...prev, [key]: date || '' }));
    };
    return (
      <Root type={type} className={`input ${className}`}>
        <div className='label'>{name}</div>
        <div className='datePickerWrapper date'>
          {/* 모바일에서 캘린더가 overflow 컨테이너(.dropzone-wrapper)에 잘리는 문제 → popper에 전용 클래스를 줘서 근태신청 RangePicker처럼 하단에서 올라오는 바텀시트로 띄움(GlobalStyle .draft-date-popper) */}
          <DatePickerComponent startDate={startDate} setDate={setDate} popperClassName='draft-date-popper' mobileTouchOnly isSaveClicked={isSaveClicked} />
        </div>
      </Root>
    );
  }

  if (subCd === '23') {
    // 기안서 기안일자 (오늘)
    const toDay = dayjs(new Date()).format('YYYY-MM-DD');
    const date = userInput?.wDate?.replaceAll('-', '') || toDay;
    const setDate = () => {};
    return (
      <Root type={type} className={`input ${className}`}>
        <div className='label'>{name}</div>
        {type === 'init_form' ? (
          <div className='empty-input' />
        ) : (
          <div className='datePickerWrapper'>
            <DatePickerComponent startDate={date} setDate={setDate} disabled />
          </div>
        )}
      </Root>
    );
  }

  if (subCd === '24') {
    const key = 'note';
    return (
      <Root type={type} className={`textarea ${type} ${className}`}>
        <div className='label'>{name}</div>
        {type === 'init_form' ? (
          <div className='input-large' />
        ) : (
          <Textarea
            disabled={type !== 'edit'}
            display='flex'
            flexDirection='row'
            name={key}
            state={userInput}
            set={setUserInput}
            getBorderStyle={isSaveClicked ? applyBorderStyle(userInput[key], 'red', key) : undefined}
          />
        )}
      </Root>
    );
  }

  if (subCd === '25') {
    const key = 'title';
    return (
      <Root type={type} className={`textarea ${type} ${className}`}>
        <div className='label'>{name}</div>
        {type === 'init_form' ? (
          <div className='input-large' />
        ) : (
          <Textarea
            disabled={type !== 'edit'}
            display='flex'
            flexDirection='row'
            name={key}
            state={userInput}
            set={setUserInput}
            getBorderStyle={isSaveClicked ? applyBorderStyle(userInput[key], 'red', key) : undefined}
          />
        )}
      </Root>
    );
  }

  if (subCd === '8' || subCd === '9') {
    const key = subCd === '8' ? 'caReason' : subCd === '9' ? 'caReplacement' : 'content';
    return (
      <Root type={type} className={`textarea ${type} ${className}`}>
        <div className='label'>{name}</div>
        {type === 'init_form' ? (
          <div className='input-large' />
        ) : (
          <Textarea
            disabled={type !== 'edit'}
            display='flex'
            flexDirection='row'
            name={key}
            state={userInput}
            set={setUserInput}
            getBorderStyle={isSaveClicked ? applyBorderStyle(userInput[key], 'red', key) : undefined}
          />
        )}
      </Root>
    );
  }

  if (subCd === '10') {
    const formWDate = data?.wDate?.split('-') ?? ['', '', ''];
    return (
      <Root type={type} className={`sender flex-center ${className}`}>
        <div>상기와 같이 근태계를 제출하오니 재가하여 주시기 바랍니다.</div>
        {type === 'init_form' ? (
          <div>
            20
            <span className='empty-span' />년 <span className='empty-span' />월 <span className='empty-span' />일
          </div>
        ) : (
          <div>
            {formWDate[0]} 년<span className='empty-span' />
            {formWDate[1]} 월<span className='empty-span' />
            {formWDate[2]} 일<span className='empty-span' />
          </div>
        )}
        <div style={{ position: 'relative' }}>
          신청자 : <span className='empty-span' />
          {data?.caName ?? ''}
          <span className='empty-span' />
          <span style={{ position: 'relative' }}>
            (서명)
            {data?.signImg && <img src={data.signImg} style={{ width: '6rem', height: 'auto', position: 'absolute', left: '-2rem', top: '-1.5rem', zIndex: 1 }} alt='user_sign_img' />}
          </span>
        </div>
      </Root>
    );
  }

  if (subCd === '11') {
    const readOnly = !cell.isDropped;

    if (type === 'init_form') {
      return (
        <Root type={type} className={`editor ${className}`}>
          <div className='label-wrapper'>
            <div className='label'>전달 사항 입력 ( {readOnly ? '우측에 배치 후 입력 가능합니다.' : '저장 버튼 클릭 시 내용이 저장됩니다.'} )</div>
          </div>
          <div className='editor-wrapper'>
            <CKEditor
              config={shortCkeditorConfig as any}
              editor={ClassicEditor as any}
              data={cell.aContents}
              disabled={readOnly}
              onReady={(editor: any) => {
                const toolbarElement = editor.ui.view.toolbar.element;
                editor.disableReadOnlyMode(editor.id);
                toolbarElement.style.display = 'flex';
              }}
              onChange={(event: any, editor: any) => {
                const contentData = editor.getData();
                setCells((prev) => prev.map((c) => (cell.subCd === c.subCd ? { ...c, aContents: contentData } : c)));
              }}
            />
          </div>
        </Root>
      );
    }
    return (
      <Root type={type} className={`editor ${className}`}>
        <div className='label-wrapper'>
          <div className='label'>전달 사항</div>
        </div>
        {cell.aContents && <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cell.aContents) }} />}
      </Root>
    );
  }

  if (subCd === '12' || subCd === '16' || subCd === '17')
    return (
      <Root type={type} className={`plain-text center title-large ${className}`}>
        {name}
      </Root>
    );

  if (subCd === '13')
    return (
      <Root type={type} className={`plain-text center ${className}`}>
        {name}
      </Root>
    );

  if (subCd === '14') {
    return (
      <Root type={type} className={`${logo ? 'logo' : 'plain-text center'} ${className}`}>
        {logo ? <img src={logo} alt='logo' /> : <div>로고가 없습니다</div>}
      </Root>
    );
  }

  if (subCd === '15') {
    // 모바일 한 줄 배치: 성명·소속과 같은 「라벨 | 값」 구조로 표시 (PC·인쇄는 기존 「문서 번호 : 값」 글자)
    if (className === 'stacked') {
      return (
        <Root type={type} className={`input ${className}`}>
          <div className='label'>{name}</div>
          <div className='empty-input'>{userInput?.caCd}</div>
        </Root>
      );
    }
    return (
      <Root type={type} className={`plain-text left ${className}`}>
        {name} : {userInput?.caCd}
      </Root>
    );
  }

  if (subCd === '18') {
    const readOnly = type !== 'edit';
    return (
      <Root type={type} className={`editor subcd_${cell.subCd} ${className}`}>
        <div className='editor-wrapper'>
          <CKEditor
            key={type}
            config={shortCkeditorConfig as any}
            editor={ClassicEditor as any}
            data={editorContent}
            disabled={readOnly}
            onReady={(editor: any) => {
              setEditorToolbarVisible(editor, type === 'init_form' || type === 'edit');
              // if (readOnly) {
              //   editor.enableReadOnlyMode(editor.id);
              //   toolbarElement.style.display = 'none';
              // } else {
              //   editor.disableReadOnlyMode(editor.id);
              //   toolbarElement.style.display = 'flex';
              // }

              // 인쇄본은 CSS 캐스케이드(38rem 고정 높이 등)에 기대지 않고, 인라인 스타일로 직접
              // 고정 높이/스크롤을 제거해 내용 길이만큼 늘어나도록 강제함 (여러 페이지 인쇄를 위해)
              if (className === 'print') {
                forcePrintEditorAutoHeight(editor);
              }
            }}
            onChange={(event: any, editor: any) => {
              const contentData = editor.getData();
              setEditorContent && setEditorContent(contentData);
            }}
          />
        </div>
      </Root>
    );
  }

  if (subCd === '21') {
    return (
      <Root type={type} className={`textarea ${type} ${className}`}>
        <div className='label'>{name}</div>
        <div className='editor-wrapper'>
          <CKEditor
            key={type}
            config={shortCkeditorConfigWithoutImg as any}
            editor={ClassicEditor as any}
            data={editorContent}
            onReady={(editor: any) => {
              if (type === 'edit') {
                editor.disableReadOnlyMode(editor.id);
                setEditorToolbarVisible(editor, true);
              } else {
                editor.enableReadOnlyMode(editor.id);
                setEditorToolbarVisible(editor, false);
              }

              if (className === 'print') {
                forcePrintEditorAutoHeight(editor);
              }
            }}
            onChange={(event: any, editor: any) => {
              const contentData = editor.getData();
              // CKEditor는 data prop이 바뀌면 이전 렌더의 onChange를 즉시 호출한다.
              // 클로저의 userInput을 펼치면 + 등록으로 막 비운 제목·비고를 이전 문서 값으로 되돌리므로 최신 상태에 합친다
              setUserInput((prev: any) => ({ ...prev, content: contentData || '' }));
            }}
          />
        </div>
      </Root>
    );
  }

  return <Root type={type}>{`${name}(${subCd})`}</Root>;
};

export default ApplicationFormItems;
